import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import FormBuilder from '../../components/app/formBuilder/index'
import CustomDrawer from '../../components/common/drawer'
import CustomeSideViewer from '../../components/common/drawer/customeSideViewer'
import { useSnackbarManager } from '../../components/common/snackbar'
import { useAuthStore } from '../../store/authStore'
import {
  getAppList,
  getBglAuthorizeUrl,
  getBglToken,
  updateBglToken,
  useEditMyProfile,
} from './api'
import { getProfileDetails } from './api'
import { myProfileSchema, MyProfileSchema } from './schema'

type Props = {
  isDrawerOpen: boolean
  disabled?: boolean
  handleClose: () => void
  handleRefresh?: () => void
  paramsId?: any
  handleCallback?: () => void
  model_name?: string
  rowData?: any
  isOwnTask?: boolean
  isGeneral?: boolean
  viewMode?: boolean
  setViewMode?: (value: boolean) => void
  edit?: boolean
  hasPermission?: boolean
  setEdit?: (value: boolean) => void
  subSection?: boolean
  setEditViewIndicator?: (value: boolean) => void
  editViewIndicator?: boolean
}
export default function MyProfileDrawer({
  isDrawerOpen,
  handleClose,
  edit,
  viewMode,
  setViewMode,
  setEdit,
  setEditViewIndicator,
}: Props) {
  const { enqueueSnackbar } = useSnackbarManager()
  const [data, setData] = useState<any>()
  const { userData } = useAuthStore()
  const [params] = useSearchParams()

  useEffect(() => {
    if (params.has('code') && params.get('code')) {
      getBglToken({ code: params.get('code') })
        .then((res) => {
          updateBglToken({
            access_token: res?.token_data?.access_token,
            refresh_token: res?.token_data?.refresh_token,
            bgl_code: params.get('code'),
            scope: res?.token_data?.scope,
          }).then((res) => {
            if (res.status) {
              window.location.href = '/home'
            }
          })
        })
        .catch((err) => {
          enqueueSnackbar(err?.response?.data?.detail, {
            variant: 'error',
          })
        })
    }
  }, [params])

  const onInit = () => {
    return {
      name: data?.name,
      password: undefined,
      phone: data?.phone,
      old_password: undefined,
      confirm_password: undefined,
      reset_password: false,
      accounting_app_id: data?.accounting_app ?? null,
      accounting_app: data?.accounting_app_data?.name ?? null,
    }
  }

  const getAccountAppList = async () => {
    const response = await getAppList({
      connect_show: true,
    })
    return response?.data.map((item: any) => ({
      id: item.id,
      name: `${item.name}`,
    }))
  }

  const handleClearAndClose = () => {
    methods.reset({
      ...onInit(),
    })
    handleClose()
  }

  const methods = useForm<MyProfileSchema>({
    resolver: zodResolver(myProfileSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...onInit(),
    },
  })

  const { watch } = methods
  const { accounting_app } = watch()

  useEffect(() => {
    methods.reset({
      ...onInit(),
    })
  }, [data])
  const {
    handleSubmit,
    trigger,
    formState: {},
  } = methods
  const { reset_password, password, confirm_password } = methods.watch()

  const [resetPasswordClicked, setResetPasswordClicked] = useState(false)

  const handleReset = () => {
    setResetPasswordClicked(!resetPasswordClicked)
    methods.setValue('reset_password', !reset_password)
  }

  const handleAuthorize = () => {
    onSubmit(methods.getValues())
    getBglAuthorizeUrl()
      .then((res) => {
        if (res.status) {
          window.open(res.url, '_blank')
        }
      })
      .catch((err) => {
        enqueueSnackbar(err?.response.data.error?.message[0], {
          variant: 'error',
        })
      })
  }

  useEffect(() => {
    trigger('confirm_password')
    trigger('password')
  }, [password, confirm_password])

  const getData = async () => {
    getProfileDetails()
      .then((res) => {
        setData(res)
      })
      .catch((err) => {
        enqueueSnackbar(err?.response.data.error?.message[0], {
          variant: 'error',
        })
      })
  }

  useEffect(() => {
    getData()
  }, [userData?.id])

  const textField = (
    name: string,
    label: string,
    placeholder: string,
    required = false,
    disabled = false
  ) => ({
    name,
    label,
    id: name,
    type: 'text',
    placeholder,
    ...(required ? { required: true } : {}),
    ...(disabled ? { disabled: true } : {}),
  })
  const formBuilderProps = [
    {
      ...textField('name', 'Name', 'Enter Name', true),
      value: data?.name,
      hidden: false,
    },
    {
      ...textField('phone', 'Phone', 'Enter Phone', false),
      value: data?.phone,
    },
    {
      name: 'reset_password',
      label: 'Change Password',
      id: 'reset_password',
      desc: 'reset_password',
      descId: 'value',
      type: 'button',
      onClick: handleReset,
      value: false,
      hidden: !reset_password && !edit,
    },
    {
      ...textField('old_password', 'Old Password', 'Enter Old Password', true),
      type: 'password',
      hidden: !reset_password || viewMode,
    },
    {
      ...textField('password', 'Password', 'Enter Password', true),
      type: 'password',
      hidden: !reset_password || viewMode,
    },
    {
      ...textField(
        'confirm_password',
        'Confirm Password',
        'Enter Confirm Password',
        true
      ),
      type: 'password',
      hidden: !reset_password || viewMode,
    },
    {
      type: 'heading',
      value: 'API Authorization',
      className: 'pt-5',
      hidden: userData?.role?.name == 'user' ? false : true,
    },
    {
      name: 'accounting_app',
      label: 'Activity Name',
      required: false,
      getData: getAccountAppList,
      async: true,
      id: 'accounting_app_id',
      paginationEnabled: false,
      descId: 'id',
      initialLoad: false,
      desc: 'name',
      type: 'auto_complete',
      placeholder: 'Select Accounting App',
      hidden: userData?.role?.name == 'user' ? false : true,
    },
    {
      name: 'authorize',
      label: 'Click here to authorize',
      id: 'authorize',
      desc: 'authorize',
      descId: 'value',
      type: 'button',
      onClick: handleAuthorize,
      value: false,
      className: 'btn text-xxs p-2 view',
      hidden:
        accounting_app == 'BGL 360' && !data?.bgl_connect_status ? false : true,
    },
    {
      type: 'heading',
      value: 'Connected',
      className: 'text-x p-2 bg-primary text-white text-center rounded-sm',
      hidden:
        accounting_app == 'BGL 360' && data?.bgl_connect_status ? false : true,
    },
  ]

  const handleSubmission = () => {
    methods.reset({
      ...onInit(),
    })
    getData()
    setEdit?.(false)
    setViewMode?.(true)
  }

  const { mutate, isLoading } = useEditMyProfile(handleSubmission)
  const onSubmit = (detail: MyProfileSchema) => {
    if (edit && reset_password) {
      if (!detail?.password) {
        methods?.setError('password', { type: 'manual', message: 'Required' })
        return
      }
      if (!detail.old_password) {
        methods.setError('old_password', {
          type: 'manual',
          message: 'Required',
        })
        return
      }
      if (!detail.confirm_password) {
        methods.setError('confirm_password', {
          type: 'manual',
          message: 'Required',
        })
        return
      }
    }

    const result = {
      name: detail?.name,
      email: data?.email,
      phone: detail?.phone,
      password: detail?.password ?? undefined,
      old_password: detail?.old_password ?? undefined,
      confirm_password: detail?.confirm_password ?? undefined,
      accounting_app:
        detail?.accounting_app_id !== '' ? detail?.accounting_app_id : null,
    }

    mutate({ data: result })
  }

  const handleChangeMode = () => {
    setViewMode?.(false)
    setEdit?.(true)
    setEditViewIndicator?.(true)
  }

  const viewHeaderData = {
    title: `${data?.name}`,
    subTitle: data?.email,
  }

  const viewContentData = [
    {
      title: 'Phone',
      value: data?.phone,
    },
  ]

  return (
    <CustomDrawer
      viewMode={viewMode}
      className="formDrawer"
      open={isDrawerOpen}
      actionLabel={viewMode ? 'Edit' : 'Save'}
      handleClose={() => handleClearAndClose()}
      actionLoader={isLoading}
      handleSubmit={
        viewMode ? handleChangeMode : handleSubmit((data) => onSubmit(data))
      }
      title={edit ? 'Edit My Profile' : 'My Profile'}
    >
      <div className="flex flex-col gap-4">
        {edit ? (
          <>
            <FormProvider {...methods}>
              <FormBuilder data={formBuilderProps} edit={true} />
            </FormProvider>
          </>
        ) : (
          <CustomeSideViewer
            headerData={viewHeaderData}
            contentData={viewContentData}
          />
        )}
      </div>
    </CustomDrawer>
  )
}
